'use client'

import {
  GetProjectFiltersQuery,
  GetUserShowcasesDocument,
  UserProfileFragment,
  WalletUserFragment
} from '@/gql/generated/graphql'
import { UserTokenGrid } from './user-token-grid/user-token-grid'
import { ShowcaseGrid } from './showcase-grid/showcase-grid'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/app/components/tabs-toggle'
import React from 'react'
import ProfileHeader from './profile-header'
import { BodyText, SquaresFourIcon, StarIcon } from 'design-configuration/components'
import { useIsProfileOwner } from './profile-context'
import { cn } from '@/lib/utils'
import { useScrollReset } from '@/hooks/use-scroll-reset'
import { useQuery } from '@urql/next'
import { MixpanelPageTracker } from '@components/analytics-page-tracker'
import { MixpanelPageNames } from '@utils/analytics'

type UserProfileProps = {
  profile?: UserProfileFragment
  walletUser?: WalletUserFragment
  projectFiltersData?: GetProjectFiltersQuery
}

export const UserProfile = ({ profile, walletUser, projectFiltersData }: UserProfileProps): React.ReactNode => {
  // Reset scroll position to top when component mounts
  useScrollReset()

  const isProfileOwner = useIsProfileOwner()
  // get user's showcase data to determine tab view state
  const profileId = profile?.id
  // Query for user showcases
  const [showcasesRes, reexecuteShowcases] = useQuery({
    query: GetUserShowcasesDocument,
    variables: { profileId: profileId ?? -1 } // -1 returns no showcases for non-profile pages
  })

  if (!profile && !walletUser) {
    return <div>No profile found</div>
  }

  const walletAddresses = profile
    ? [...(profile?.linked_accounts?.map((account) => account.public_address) ?? [])]
    : [walletUser?.public_address ?? '']

  // Extract social links from profile
  const socialLinks = {
    twitter: profile?.twitter_username,
    website: profile?.external_website,
    instagram: profile?.instagram_username,
    discord: profile?.discord_username
  }

  const { data: showcaseData, fetching: showcaseFetching } = showcasesRes
  // MVP: Only show single showcase
  const showcase = showcaseData?.showcases?.[0]
  const showcaseItems = showcase?.showcase_items ?? []
  const hasShowcase = showcaseItems.length > 0

  // only show single tab if no showcase and not profile owner
  const isSingleTabMode = !hasShowcase && !isProfileOwner

  // order of tabs depends on showcase state
  const ArtworksTab = (
    <TabsTrigger
      key="artworks"
      value="artworks"
      className={cn(
        'bg-surface-low data-[state=active]:bg-surface text-on-surface-variant data-[state=active]:text-on-surface fill-on-surface-variant data-[state=active]:fill-on-surface shadow-none transition-colors',
        // Single tab styling - will automatically revert to multi-tab styling when Showcase tab is added back
        isSingleTabMode
          ? 'px-md cursor-default justify-start text-left'
          : 'hover:data-[state=inactive]:fill-on-surface hover:data-[state=inactive]:text-on-surface'
      )}
    >
      <SquaresFourIcon className="mr-xs h-5 w-5" />
      <BodyText className={cn(isSingleTabMode ? 'inline font-normal' : 'hidden font-normal sm:inline')}>
        Artworks
      </BodyText>
    </TabsTrigger>
  )
  const ShowcaseTab =
    profileId && (hasShowcase || isProfileOwner) ? (
      <TabsTrigger
        key="showcase"
        value="showcase"
        className="bg-surface-low data-[state=active]:bg-surface text-on-surface-variant data-[state=active]:text-on-surface fill-on-surface-variant data-[state=active]:fill-on-surface hover:data-[state=inactive]:fill-on-surface hover:data-[state=inactive]:text-on-surface shadow-none transition-colors"
      >
        <StarIcon className="mr-xs h-5 w-5" />
        <BodyText className="hidden font-normal sm:inline">Showcase</BodyText>
      </TabsTrigger>
    ) : null

  const orderedTabs = isSingleTabMode
    ? [ArtworksTab]
    : hasShowcase
      ? [ShowcaseTab, ArtworksTab] // showcase tab first if it exists
      : [ArtworksTab, ShowcaseTab] // artworks tab first if no showcase

  return (
    <div className="p-sm pb-2xl">
      <MixpanelPageTracker pageName={MixpanelPageNames.profile} />
      <ProfileHeader username={profile?.username ?? ''} socialLinks={socialLinks} bio={profile?.bio ?? ''} />

      <div className="border-outline-variant rounded-xs border">
        <Tabs key={showcaseFetching ? 'loading' : 'loaded'} defaultValue={hasShowcase ? 'showcase' : 'artworks'}>
          <TabsList
            className={cn(
              'border-outline-variant border-b-1 rounded-none border-l-0 border-r-0 border-t-0 transition-opacity duration-300',
              showcaseFetching ? 'opacity-50' : 'opacity-100'
            )}
          >
            {orderedTabs.map((tab) => tab)}
          </TabsList>
          {!isSingleTabMode && profileId ? (
            <TabsContent value="showcase" className="transition-opacity duration-300 ease-in-out">
              <div
                className={cn(
                  'transition-opacity duration-300 ease-in-out',
                  showcaseFetching ? 'opacity-50' : 'opacity-100'
                )}
              >
                <ShowcaseGrid
                  profileId={profileId}
                  walletAddresses={walletAddresses}
                  isProfileOwner={isProfileOwner}
                  showcase={showcase}
                  showcaseFetching={showcaseFetching}
                  reexecuteShowcases={reexecuteShowcases}
                  projectFiltersData={projectFiltersData}
                />
              </div>
            </TabsContent>
          ) : null}
          <TabsContent value="artworks" className="transition-opacity duration-300 ease-in-out">
            <div
              className={cn(
                'transition-opacity duration-300 ease-in-out',
                showcaseFetching ? 'opacity-50' : 'opacity-100'
              )}
            >
              <UserTokenGrid walletAddresses={walletAddresses} projectFiltersData={projectFiltersData} />
            </div>
          </TabsContent>
          {/* FUTURE: Add more tabs here, activity, etc. */}
        </Tabs>
      </div>
    </div>
  )
}
